(() => {
  const mount = (scope) => { scope.querySelectorAll('[data-moonlit-product-section]').forEach((section) => {
    if (section.dataset.pageRuntime === 'moonlit') return;
    section.dataset.pageRuntime = 'moonlit';
    const controller = new AbortController();
    const disposals = [];
    const listen = (target, type, handler, options = {}) => target?.addEventListener(type, (event) => {
      if (section.isConnected) handler(event);
    }, { ...options, signal: controller.signal });
    const previousDispose = section.miaDispose;
    section.miaDispose = () => { previousDispose?.(); controller.abort(); disposals.forEach(dispose => dispose()); };
    const materialRows = Array.from(section.querySelectorAll('[data-piece-materials]'));
    if (materialRows.length && section.dataset.materialsInitialized !== 'true') {
      section.dataset.materialsInitialized = 'true';
      const materialTips = materialRows.map((row) => {
        const trigger = row.querySelector('[data-piece-materials-trigger]');
        const content = row.querySelector('[data-piece-materials-content]');
        document.body.append(content);
        disposals.push(() => content.remove());
        return { trigger, content };
      });
      const closeMaterialTips = () => {
        materialTips.forEach(({ trigger, content }) => {
          trigger.setAttribute('aria-expanded', 'false');
          content.setAttribute('aria-hidden', 'true');
          content.classList.remove('is-open');
        });
      };
      const positionMaterialTip = ({ trigger, content }) => {
        const viewport = window.visualViewport;
        const minLeft = (viewport?.offsetLeft || 0) + 16;
        const minTop = (viewport?.offsetTop || 0) + 16;
        const maxRight = minLeft + (viewport?.width || window.innerWidth) - 32;
        const maxBottom = minTop + (viewport?.height || window.innerHeight) - 32;
        const rect = trigger.getBoundingClientRect();
        if (rect.bottom <= minTop || rect.top >= maxBottom) {
          closeMaterialTips();
          return;
        }
        const width = content.offsetWidth;
        const height = content.offsetHeight;
        const anchor = trigger.querySelector('.product-order-inclusions__info').getBoundingClientRect();
        const anchorX = anchor.left + anchor.width / 2;
        const left = Math.max(minLeft, Math.min(anchorX - width / 2, maxRight - width));
        const above = rect.top - height - 12 >= minTop;
        const top = above ? rect.top - height - 12 : Math.min(rect.bottom + 12, maxBottom - height);
        content.style.left = `${left}px`;
        content.style.top = `${Math.max(minTop, top)}px`;
        content.style.setProperty('--material-tooltip-arrow', `${Math.max(14, Math.min(width - 28, anchorX - left - 7))}px`);
        content.dataset.placement = above ? 'above' : 'below';
      };
      materialTips.forEach((tip) => {
        const { trigger, content } = tip;
        trigger.addEventListener('click', () => {
          const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';
          closeMaterialTips();
          if (!shouldOpen) return;
          positionMaterialTip(tip);
          trigger.setAttribute('aria-expanded', 'true');
          content.setAttribute('aria-hidden', 'false');
          content.classList.add('is-open');
        });
        trigger.addEventListener('blur', closeMaterialTips);
      });
      listen(document, 'pointerdown', (event) => {
        if (!materialTips.some(({ trigger }) => trigger.contains(event.target))) closeMaterialTips();
      });
      section.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMaterialTips();
      });
      section.addEventListener('click', (event) => {
        if (event.target.closest('summary')) closeMaterialTips();
      });
      const positionOpenMaterialTips = () => {
        materialTips.forEach((tip) => {
          if (tip.content.classList.contains('is-open')) positionMaterialTip(tip);
        });
      };
      listen(window, 'scroll', positionOpenMaterialTips, { passive: true });
      listen(window, 'resize', closeMaterialTips, { passive: true });
      listen(window.visualViewport, 'resize', closeMaterialTips, { passive: true });
      listen(window.visualViewport, 'scroll', positionOpenMaterialTips, { passive: true });
    }
    const suggestions = section.querySelector('[data-product-suggestions]');
    if (suggestions && suggestions.dataset.initialized !== 'true') {
      const suggestionGrid = suggestions.querySelector('[data-product-suggestions-grid]');
      const suggestionTemplates = Array.from(suggestions.querySelectorAll('[data-product-suggestion-template]'));

      suggestions.dataset.initialized = 'true';
      for (let index = suggestionTemplates.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [suggestionTemplates[index], suggestionTemplates[randomIndex]] = [suggestionTemplates[randomIndex], suggestionTemplates[index]];
      }

      const suggestionCards = document.createDocumentFragment();
      suggestionTemplates.forEach((template) => {
        suggestionCards.appendChild(template.content.cloneNode(true));
      });
      suggestionGrid.appendChild(suggestionCards);
      suggestionTemplates.forEach((template) => template.remove());
      if (suggestionGrid.children.length > 0) suggestions.hidden = false;
    }

    const gallery = section.querySelector('[data-product-gallery]');
    if (gallery) {
      const track = gallery.querySelector('[data-product-gallery-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-product-gallery-slide]'));
      const cards = Array.from(gallery.querySelectorAll('[data-product-gallery-card]'));
      const dots = Array.from(gallery.querySelectorAll('[data-product-gallery-dot]'));
      const dotNavigation = gallery.querySelector('.product-gallery__dots');
      const indicator = gallery.querySelector('[data-product-gallery-indicator]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const compactGallery = window.matchMedia('(max-width: 520px), (hover: none) and (pointer: coarse)');
      const nativeZoomActive = () => document.documentElement.hasAttribute('data-product-zoom-active');
      const navigationDurationValue = getComputedStyle(gallery).getPropertyValue('--gallery-navigation-duration').trim();
      const parsedNavigationDuration = Number.parseFloat(navigationDurationValue);
      const navigationDuration = Number.isFinite(parsedNavigationDuration)
        ? (navigationDurationValue.endsWith('ms') ? parsedNavigationDuration : parsedNavigationDuration * 1000)
        : 420;
      let scrollFrame;
      let resizeFrame;
      let movementTimer;
      let navigationFrame;
      let navigationFade;
      let navigationVelocity = 0;
      let navigationTarget = -1;
      let lastActiveIndex = -1;
      let lastTargetMediaId = null;
      let tidalStylesApplied = false;
      let galleryGeometry;

      const refreshGalleryGeometry = () => {
        const clientWidth = track.clientWidth;
        const maximumScroll = Math.max(0, track.scrollWidth - clientWidth);
        const centers = slides.map((slide) => slide.offsetLeft + (slide.offsetWidth / 2));
        galleryGeometry = {
          centers,
          dotCenters: dots.map((dot) => dot.offsetLeft + (dot.offsetWidth / 2)),
          clientWidth,
          maximumScroll,
          step: centers.length > 1 ? Math.max(1, centers[1] - centers[0]) : clientWidth
        };
      };

      const measureGallery = () => {
        if (!galleryGeometry) refreshGalleryGeometry();
        const scrollLeft = Math.max(0, Math.min(track.scrollLeft, galleryGeometry.maximumScroll));
        return {
          ...galleryGeometry,
          scrollLeft,
          viewportCenter: scrollLeft + (galleryGeometry.clientWidth / 2)
        };
      };

      const updateZoomVisibility = () => {
        const geometry = measureGallery();
        slides.forEach((slide, index) => {
          const halfWidth = slide.offsetWidth / 2;
          const left = geometry.centers[index] - halfWidth;
          const right = geometry.centers[index] + halfWidth;
          slide.toggleAttribute('data-zoom-visible', right > geometry.scrollLeft && left < geometry.scrollLeft + geometry.clientWidth);
        });
        gallery.setAttribute('data-gallery-zoom-limited', '');
      };

      const activeIndex = (geometry = measureGallery()) => geometry.centers.reduce((closest, center, index) => {
        const distance = Math.abs(center - geometry.viewportCenter);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Infinity }).index;

      const setActiveDot = (index) => {
        const targetMediaId = navigationTarget >= 0 ? slides[navigationTarget]?.dataset.mediaId : null;
        if (index === lastActiveIndex && targetMediaId === lastTargetMediaId) return;
        lastTargetMediaId = targetMediaId;
        dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
        lastActiveIndex = index;
        gallery.dataset.activeMediaId = slides[index]?.dataset.mediaId || '';
        section.dispatchEvent(new CustomEvent('product:gallery-change', {
          detail: {
            mediaId: gallery.dataset.activeMediaId,
            // Keep product copy on the destination while passing other slides.
            targetMediaId
          }
        }));
      };

      const updateIndicator = (geometry) => {
        if (!indicator || geometry.dotCenters.length < 2) return;
        const position = Math.max(0, Math.min(slides.length - 1, (geometry.viewportCenter - geometry.centers[0]) / geometry.step));
        const lower = Math.floor(position);
        const upper = Math.min(lower + 1, geometry.dotCenters.length - 1);
        const center = geometry.dotCenters[lower] + ((geometry.dotCenters[upper] - geometry.dotCenters[lower]) * (position - lower));
        indicator.style.transform = `translate3d(${(center - 10).toFixed(3)}px, 0, 0)`;
      };

      const resetTidalCards = () => {
        if (!tidalStylesApplied) return;
        gallery.classList.remove('is-tidal-moving');
        slides.forEach((slide, index) => {
          slide.style.removeProperty('z-index');
          slide.classList.remove('is-tidal-centered');
          const card = cards[index];
          if (!card) return;
          card.style.removeProperty('--gallery-rotate');
          card.style.removeProperty('--gallery-scale');
          card.style.removeProperty('--gallery-lift');
          card.style.removeProperty('--gallery-origin');
          card.style.removeProperty('--gallery-refraction');
          card.style.removeProperty('--gallery-refraction-x');
          card.classList.remove('is-tidal-near');
          card.classList.remove('is-tidal-centered');
        });
        tidalStylesApplied = false;
      };

      const updateTidalCards = (geometry) => {
        if (compactGallery.matches || reducedMotion.matches || slides.length < 2) {
          resetTidalCards();
          return;
        }

        const styles = geometry.centers.map((center) => {
          const rawDistance = (center - geometry.viewportCenter) / geometry.step;
          const distance = Math.max(-1, Math.min(1, rawDistance));
          const depth = Math.abs(distance);
          const wave = Math.sin(Math.PI * depth);
          const angle = 4 * Math.sin(Math.PI * distance);
          const refraction = .18 * wave;
          const refractionPosition = distance < 0
            ? 620 - (625 * depth)
            : -5 + (625 * depth);
          return {
            angle,
            centered: Math.abs(rawDistance) <= .01,
            depth,
            origin: distance < 0 ? 'right center' : 'left center',
            near: Math.abs(rawDistance) <= 1.05,
            refraction,
            refractionX: `${refractionPosition.toFixed(1)}%`
          };
        });

        styles.forEach((style, index) => {
          const slide = slides[index];
          const card = cards[index];
          if (!card || (!style.near && !card.classList.contains('is-tidal-near'))) return;
          slide.style.zIndex = String(100 - Math.round(style.depth * 10));
          card.style.setProperty('--gallery-rotate', `${style.angle.toFixed(3)}deg`);
          card.style.setProperty('--gallery-scale', (1 - (.03 * style.depth)).toFixed(4));
          card.style.setProperty('--gallery-lift', `${(4 * style.depth).toFixed(2)}px`);
          card.style.setProperty('--gallery-origin', style.origin);
          card.style.setProperty('--gallery-refraction', style.refraction.toFixed(3));
          card.style.setProperty('--gallery-refraction-x', style.refractionX);
          slide.classList.toggle('is-tidal-centered', style.centered);
          card.classList.toggle('is-tidal-near', style.near);
          card.classList.toggle('is-tidal-centered', style.centered);
        });
        tidalStylesApplied = true;
      };

      const updateGallery = () => {
        if (nativeZoomActive()) return;
        const geometry = measureGallery();
        setActiveDot(activeIndex(geometry));
        updateIndicator(geometry);
        updateTidalCards(geometry);
      };

      const cancelNavigation = (update = true) => {
        navigationFade?.cancel();
        navigationFade = null;
        if (navigationFrame) cancelAnimationFrame(navigationFrame);
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = null;
        navigationFrame = null;
        navigationVelocity = 0;
        navigationTarget = -1;
        track.classList.remove('is-navigating');
        if (update) updateGallery();
      };

      const goTo = (index, behavior = 'smooth', releaseVelocity = navigationVelocity) => {
        if (nativeZoomActive()) return;
        const target = Math.max(0, Math.min(index, slides.length - 1));
        if (navigationTarget === target) return;
        const geometry = measureGallery();
        const destination = Math.max(0, Math.min(geometry.centers[target] - (geometry.clientWidth / 2), geometry.maximumScroll));
        const startingPosition = geometry.scrollLeft;
        const distance = destination - startingPosition;
        if (Math.abs(distance) <= .35) {
          cancelNavigation(false);
          setActiveDot(target);
          updateGallery();
          return;
        }

        cancelNavigation(false);

        if (reducedMotion.matches || behavior === 'auto') {
          track.scrollLeft = destination;
          setActiveDot(target);
          updateGallery();
          return;
        }

        navigationTarget = target;
        track.classList.add('is-navigating');
        // Variant choices should not travel through unrelated product photos.
        if (behavior === 'variant' && Math.abs(distance) > geometry.step * 1.1) {
          track.scrollLeft = destination;
          updateGallery();
          const fade = track.animate([{ opacity: .25 }, { opacity: 1 }], {
            duration: 180,
            easing: 'cubic-bezier(.2,.7,.2,1)'
          });
          navigationFade = fade;
          fade.finished.then(() => {
            if (navigationFade !== fade) return;
            navigationFade = null;
            navigationTarget = -1;
            track.classList.remove('is-navigating');
            updateGallery();
          }).catch(() => {});
          return;
        }
        if (!compactGallery.matches) gallery.classList.add('is-tidal-moving');
        const duration = Math.min(600, navigationDuration + (60 * Math.max(0, Math.abs(distance) / geometry.step - 1)));
        const initialSlope = Math.max(0, Math.min(1.5, releaseVelocity * duration / distance));

        let startedAt;
        const animateNavigation = (now) => {
          if (nativeZoomActive()) { cancelNavigation(false); return; }
          if (startedAt === undefined) startedAt = now;
          const progress = Math.min(1, Math.max(0, (now - startedAt) / duration));
          const eased = progress * progress * (3 - (2 * progress)) + initialSlope * progress * (1 - progress) * (1 - progress);
          navigationVelocity = distance / duration * (6 * progress * (1 - progress) + initialSlope * (1 - progress) * (1 - 3 * progress));
          track.scrollLeft = startingPosition + (distance * eased);
          updateGallery();

          if (progress < 1) {
            navigationFrame = requestAnimationFrame(animateNavigation);
            return;
          }

          track.scrollLeft = destination;
          navigationFrame = null;
          navigationVelocity = 0;
          navigationTarget = -1;
          track.classList.remove('is-navigating');
          gallery.classList.remove('is-tidal-moving');
          updateGallery();
        };
        navigationFrame = requestAnimationFrame(animateNavigation);
      };

      dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.index))));
      track.addEventListener('keydown', (event) => {
        if (event.target.closest('mae-gallery-video')) return;
        const currentIndex = navigationTarget >= 0 ? navigationTarget : activeIndex();
        if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(currentIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); goTo(currentIndex + 1); }
      });
      track.addEventListener('scroll', () => {
        if (nativeZoomActive()) { updateZoomVisibility(); return; }
        if (!compactGallery.matches && !reducedMotion.matches && slides.length > 1) {
          gallery.classList.add('is-tidal-moving');
          window.clearTimeout(movementTimer);
          movementTimer = window.setTimeout(() => gallery.classList.remove('is-tidal-moving'), 140);
        }
        if (navigationFrame || scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = null;
          updateGallery();
        });
      }, { passive: true });

      let dragStartX = 0;
      let dragStartScroll = 0;
      let dragStartIndex = 0;
      let dragSamples = [];
      track.addEventListener('pointerdown', (event) => {
        if (event.target.closest('mae-gallery-video')) return;
        if (nativeZoomActive()) return;
        const mouseDrag = slides.length > 1 && event.pointerType === 'mouse' && event.button === 0;
        if (mouseDrag) track.classList.add('is-dragging');
        else if (navigationFrame && event.pointerType === 'touch') track.classList.add('is-touch-interrupt');
        cancelNavigation();
        if (!mouseDrag) return;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        dragStartIndex = activeIndex();
        dragSamples = [{ time: event.timeStamp, position: track.scrollLeft }];
        track.setPointerCapture(event.pointerId);
      });
      track.addEventListener('pointermove', (event) => {
        if (!track.classList.contains('is-dragging')) return;
        track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
        dragSamples.push({ time: event.timeStamp, position: track.scrollLeft });
        dragSamples = dragSamples.filter((sample) => event.timeStamp - sample.time <= 80);
      });
      const finishDrag = (event) => {
        if (nativeZoomActive()) return;
        if (track.classList.contains('is-touch-interrupt')) {
          if (event.type === 'pointerup') goTo(activeIndex());
          track.classList.remove('is-touch-interrupt');
          updateGallery();
        }
        if (!track.classList.contains('is-dragging')) return;
        const distance = event.clientX - dragStartX;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        const target = Math.abs(distance) > 44 ? dragStartIndex + (distance < 0 ? 1 : -1) : activeIndex();
        const firstSample = dragSamples[0];
        const lastSample = dragSamples[dragSamples.length - 1];
        const velocity = firstSample && lastSample && event.timeStamp - lastSample.time < 80
          ? (lastSample.position - firstSample.position) / Math.max(1, lastSample.time - firstSample.time)
          : 0;
        goTo(target, 'smooth', velocity);
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('wheel', () => {
        if (navigationFrame) cancelNavigation();
      }, { passive: true });
      listen(window, 'resize', () => {
        if (nativeZoomActive()) return;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          if (nativeZoomActive()) return;
          if (galleryGeometry && track.clientWidth === galleryGeometry.clientWidth) return;
          const index = lastActiveIndex >= 0 ? lastActiveIndex : activeIndex();
          cancelNavigation(false);
          refreshGalleryGeometry();
          goTo(index, 'auto');
          updateGallery();
        });
      }, { passive: true });
      listen(document, 'mia:product-zoom-change', (event) => {
        if (event.detail.active) {
          updateZoomVisibility();
          cancelNavigation(false);
          cancelAnimationFrame(resizeFrame);
          clearTimeout(movementTimer);
          track.classList.remove('is-dragging', 'is-touch-interrupt');
          gallery.classList.remove('is-tidal-moving');
          resetTidalCards();
          return;
        }
        gallery.removeAttribute('data-gallery-zoom-limited');
        slides.forEach((slide) => slide.removeAttribute('data-zoom-visible'));
        refreshGalleryGeometry();
        updateGallery();
      });
      const handleMotionPreference = () => {
        const target = navigationTarget >= 0 ? navigationTarget : activeIndex();
        cancelNavigation(false);
        goTo(target, reducedMotion.matches ? 'auto' : 'smooth');
      };
      if (reducedMotion.addEventListener) listen(reducedMotion, 'change', handleMotionPreference);
      else if (reducedMotion.addListener) reducedMotion.addListener(handleMotionPreference);
      disposals.push(() => { cancelNavigation(false); cancelAnimationFrame(scrollFrame); cancelAnimationFrame(resizeFrame); clearTimeout(movementTimer); });
      refreshGalleryGeometry();
      if (nativeZoomActive()) updateZoomVisibility();
      section.moonlitGallery = { goTo, activeIndex };
      updateGallery();
      if (indicator) dotNavigation.classList.add('has-gallery-indicator');
      section.dispatchEvent(new CustomEvent('moonlit:gallery-ready'));
    }

    const productForm = section.querySelector('form.product-form');
    if (productForm && window.customElements?.whenDefined) {
      customElements.whenDefined('shopify-payment-terms').then(() => {
        const shadow = productForm.querySelector('shopify-payment-terms')?.shadowRoot;
        if (!shadow || shadow.querySelector('[data-mia-payment-terms-style]')) return;
        const style = document.createElement('style');
        style.dataset.miaPaymentTermsStyle = '';
        style.textContent = `
          #shopify-installments-content {
            display: inline;
            color: #60605e;
            font-size: 12px;
            line-height: 1.4;
          }
          #shopify-installments-content > div {
            display: inline-flex;
            margin-inline-end: 0;
          }
          #shopify-installments-cta {
            display: inline-flex;
            margin-inline-start: 4px;
            vertical-align: middle;
          }
          #shopify-installments-content > div:last-child:has(svg) {
            margin-inline-end: 28px;
          }
          #shopify-installments-content:has(> div:last-child svg) + #shopify-installments-cta:has(svg) {
            margin-inline-start: -24px;
            width: 24px;
            box-sizing: border-box;
          }
        `;
        shadow.append(style);
      });
    }
    const addStatus = section.querySelector('[data-product-form-status]');
    const buyNowButton = section.querySelector('[data-buy-now]');
    const buyNowLabel = buyNowButton?.querySelector('[data-buy-now-label]') || buyNowButton;
    const addButton = section.querySelector('button[name="add"]');
    if (productForm && buyNowButton && addButton) {
      buyNowButton.addEventListener('click', async () => {
        if (buyNowButton.disabled || buyNowButton.getAttribute('aria-busy') === 'true') return;

        const cartRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
        const defaultLabel = buyNowButton.dataset.defaultLabel || 'Express Checkout';
        buyNowButton.disabled = true;
        addButton.disabled = true;
        buyNowButton.setAttribute('aria-busy', 'true');
        buyNowButton.setAttribute('aria-disabled', 'true');
        buyNowLabel.textContent = buyNowButton.dataset.loadingLabel || defaultLabel;
        if (addStatus) {
          addStatus.hidden = true;
          addStatus.removeAttribute('data-state');
          addStatus.textContent = '';
        }

        try {
          try {
            window.sessionStorage.removeItem('mia-promo-validation');
            window.sessionStorage.removeItem('mia-promo-feedback');
          } catch (error) {
            // Storage access must not block checkout.
          }

          try {
            const state = window.history.state && typeof window.history.state === 'object' ? { ...window.history.state } : {};
            delete state.miaPromoValidation;
            window.history.replaceState(state, '', window.location.href);
          } catch (error) {
            // History access must not block checkout.
          }

          const discountResponse = await fetch(`${cartRoot}cart/update.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: '' })
          });
          if (!discountResponse.ok) throw new Error('Unable to clear saved discount');

          const addResponse = await fetch(`${cartRoot}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(productForm)
          });
          const addData = await addResponse.json().catch(() => ({}));
          if (!addResponse.ok) throw new Error(addData.description || addData.message || section.dataset.addError);
          window.location.assign(`${cartRoot}checkout`);
        } catch (error) {
          console.error('Unable to open checkout.', error);
          if (addStatus) {
            addStatus.textContent = section.dataset.addError;
            addStatus.dataset.state = 'error';
            addStatus.hidden = false;
          }
          buyNowButton.disabled = false;
          addButton.disabled = false;
          buyNowButton.removeAttribute('aria-busy');
          buyNowButton.removeAttribute('aria-disabled');
          buyNowLabel.textContent = defaultLabel;
        }
      });
    }

    const select = section.querySelector('.product-select');
    const price = section.querySelector('[data-current-price]');
    const comparePrice = section.querySelector('[data-compare-price]');
    if (!select || !price || !comparePrice || !addButton) return;

    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const available = option.dataset.available === 'true';
      price.textContent = option.dataset.price;
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;
      addButton.disabled = !available;
      addButton.textContent = available ? section.dataset.addLabel : section.dataset.soldLabel;
      addButton.dataset.defaultLabel = addButton.textContent;
      if (buyNowButton) {
        buyNowButton.disabled = !available;
        buyNowLabel.textContent = available ? buyNowButton.dataset.defaultLabel : section.dataset.soldLabel;
      }
    });
  });
  };
  mount(document);
  document.addEventListener('mia:page:mount', event => mount(event.detail.root));
})();
